/* Части формы «Текста»: лента «Стиль текста» описания (§7.2, BotCore.styleRail), сегмент «Для чего» и ряд шаблонов промпта (§7.3),
   поле «Что именно нужно» (карточка 62 px, .tt-fld). Значения — только из формы и политики. */
import { useEffect, useRef } from 'react';
import { uiText } from '../../../i18n';
import { StudioIcon } from '../../studio/StudioIcon';
import { choiceLabels, toneCards } from './text-model';

export function TextToneRail({ values, selected, disabled, onPick }: { values: string[]; selected: string; disabled: boolean; onPick(value: string): void }) {
  return (
    <div className="bcs text-tone" id="ttTone">
      <div className="glbl bcs-gl">{uiText('Стиль текста')}</div>
      <div className="bcs-r nosb" role="radiogroup" aria-label={uiText('Стиль текста')}>
        {values.map(value => {
          const card = toneCards[value];
          const name = card?.name ?? choiceLabels[value] ?? value;
          return (
            <button
              key={value}
              type="button"
              className={`bcs-c${selected === value ? ' on' : ''}`}
              role="radio"
              aria-checked={selected === value}
              disabled={disabled}
              onClick={() => onPick(value)}
            >
              <span className="pc">
                <span className="tt-tdoc" aria-hidden="true">
                  {card && <b>{uiText(card.head)}</b>}
                  {card && <span>{uiText(card.line)}</span>}
                  <i />
                  <i className="s" />
                </span>
                {card?.badge && <span className="nb">{uiText(card.badge)}</span>}
                <i className="ck">
                  <StudioIcon kind="check" />
                </i>
              </span>
              <b>{uiText(name)}</b>
            </button>
          );
        })}
      </div>
    </div>
  );
}
/** «Для чего»: компактная таблетка по центру (.seg.sm.ap-mode.tt-for) */
export function TextForSegment({ values, selected, disabled, onPick }: { values: string[]; selected: string; disabled: boolean; onPick(value: string): void }) {
  return (
    <div className="seg sm anim ap-mode tt-for" id="ttFor" role="radiogroup" aria-label={uiText('Для чего')}>
      {values.map(value => (
        <button
          key={value}
          type="button"
          role="radio"
          aria-checked={selected === value}
          className={selected === value ? 'on' : ''}
          disabled={disabled}
          onClick={() => onPick(value)}
        >
          {uiText(choiceLabels[value] ?? value)}
        </button>
      ))}
    </div>
  );
}
/** Растворение краёв ряда там, где есть продолжение (BotCore.fade): --ml / --mr */
function useEdgeFade(ref: React.RefObject<HTMLElement | null>, deps: unknown[]) {
  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    const sync = () => {
      element.style.setProperty('--ml', element.scrollLeft > 2 ? '24px' : '0px');
      element.style.setProperty('--mr', element.scrollWidth - element.clientWidth - element.scrollLeft > 2 ? '24px' : '0px');
    };
    sync();
    element.addEventListener('scroll', sync, { passive: true });
    window.addEventListener('resize', sync);
    return () => {
      element.removeEventListener('scroll', sync);
      window.removeEventListener('resize', sync);
    };
    // Ряд пересчитывается при смене набора шаблонов
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}
/** Шаблоны одним рядом: первой «Свой вариант» (если форма публикует поле своего варианта), дальше шаблоны политики; выбранные залиты, с крестиком */
export function TextTemplates({
  label,
  templates,
  selected,
  own,
  disabled,
  onTemplate,
  onOwn,
}: {
  label: string;
  templates: string[];
  selected: string[];
  /** null — поля своего варианта форма не публикует */
  own: boolean | null;
  disabled: boolean;
  onTemplate(value: string): void;
  onOwn(): void;
}) {
  const row = useRef<HTMLDivElement>(null);
  useEdgeFade(row, [templates.join('|'), own]);
  if (!templates.length && own === null) return null;
  return (
    <div ref={row} className="tt-chips tt-tpl nosb" id="ttTpl" role="group" aria-label={uiText(label)}>
      {own !== null && (
        <button className={`chip own${own ? ' on' : ''}`} type="button" aria-pressed={own} disabled={disabled} style={{ ['--i' as string]: 0 }} onClick={onOwn}>
          <StudioIcon kind="plus" />
          <span>{uiText('Свой вариант')}</span>
          <StudioIcon kind="close" />
        </button>
      )}
      {templates.map((value, index) => (
        <button
          key={value}
          className={`chip${selected.includes(value) ? ' on' : ''}`}
          type="button"
          aria-pressed={selected.includes(value)}
          disabled={disabled}
          style={{ ['--i' as string]: index + 1 }}
          onClick={() => onTemplate(value)}
        >
          {uiText(value)}
          <StudioIcon kind="close" />
        </button>
      ))}
    </div>
  );
}
/** Поле в карточке 62 px (fldHTML прототипа): значок в круге, подпись над строкой ввода */
export function TextLineField({
  label,
  value,
  limit,
  placeholder,
  disabled,
  icon = 'edit',
  onChange,
}: {
  label: string;
  value: string;
  limit: number;
  placeholder: string;
  disabled: boolean;
  icon?: string;
  onChange(value: string): void;
}) {
  return (
    <div className="tt-w">
      <label className="tt-fld">
        <span className="ic">
          <StudioIcon kind={icon} />
        </span>
        <span className="m">
          <small>{uiText(label)}</small>
          <input
            type="text"
            autoComplete="off"
            maxLength={limit}
            placeholder={uiText(placeholder)}
            aria-label={uiText(label)}
            value={value}
            disabled={disabled}
            onChange={event => onChange(Array.from(event.target.value).slice(0, limit).join(''))}
          />
        </span>
      </label>
    </div>
  );
}
